import { useEffect, useRef } from 'react';
import clsx from 'clsx';

export type AdFormat =
  | 'auto'
  | 'fluid'
  | 'rectangle'
  | 'vertical'
  | 'horizontal';

interface GoogleAdProps {
  /**
   * AdSense 廣告單元 ID (Slot ID)。
   * 如果沒有提供，會嘗試顯示自動廣告或測試廣告，或是僅作為版位預留。
   */
  slotId?: string;
  /**
   * 廣告發佈商 ID，預設為本站的 ID。
   */
  clientId?: string;
  /**
   * 廣告格式，預設為 'auto'
   */
  format?: AdFormat;
  /**
   * 是否支援響應式，預設為 true
   */
  responsive?: boolean;
  /**
   * 額外的自訂 CSS class
   */
  className?: string;
  /**
   * 額外的 inline styles
   */
  style?: React.CSSProperties;
}

export function GoogleAd({
  slotId,
  clientId = import.meta.env.VITE_ADSENSE_CLIENT_ID ||
    'ca-pub-2972729673396419',
  format = 'auto',
  responsive = true,
  className,
  style,
}: GoogleAdProps) {
  const adRef = useRef<HTMLModElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    // 避免在開發環境或無 slotId 的情況下發生錯誤
    if (!slotId) return;

    // 防止同一個廣告區塊被重複初始化
    if (initialized.current) return;

    try {
      if (adRef.current && typeof window !== 'undefined') {
        const adsbygoogle = (window as any).adsbygoogle || [];
        adsbygoogle.push({});
        initialized.current = true;
      }
    } catch (error) {
      console.error('Google AdSense initialization failed:', error);
    }
  }, [slotId]);

  if (!slotId) {
    // 在開發階段或尚未申請到 slotId 時，顯示預留版位
    return (
      <div
        className={clsx(
          'flex items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400',
          className
        )}
        style={{ minHeight: '100px', ...style }}
      >
        [廣告版位]
      </div>
    );
  }

  return (
    <div className={clsx('overflow-hidden', className)} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', ...style }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
}
